import { Button, ButtonProps, ClickEvent, ReportPreview } from "@mcleod/components";
import { AutogenLayoutProcessingStepper } from "./autogen/AutogenLayoutProcessingStepper";
import { ProcessType } from "./ProcessingTypes";
import { Api, Currency } from "@mcleod/core";
import { CommonDialogs } from "@mcleod/common";

const nextStepBtnProps: Partial<ButtonProps> = {
    rowBreak: false, color: "default.reverse", backgroundColor: "primary",
    caption: "Post", borderColor: "primary.dark", borderWidth: 1
};

export class ProcessingStepper extends AutogenLayoutProcessingStepper {
    private reportPreviewEditStep: ReportPreview;
    private reportPreviewPostStep: ReportPreview;
    private postableCount: number;
    private postableAmount: Currency;
    public processType: ProcessType;
    public buttonNextStep: Button = new Button(nextStepBtnProps);
    public doAfterPost: (response?: any) => void;
    public getApiPayload: (posting: boolean) => any;
    public calculatePostableTotals: () => Promise<{ count: number, amount: Currency }>;

    onLoad(): void | Promise<void> {
        this.buttonNextStep.addClickListener((event: ClickEvent) => this.continue());
        this.createEditStepReportPreview();
        this.createPostStepReportPreview();
    }

    private createEditStepReportPreview() {
        this.reportPreviewEditStep = new ReportPreview(
            { reportPreviewTitle: `${this.processType.title} Edit List` },
            { id: "reportPreviewEditStep" }
        );
        this.stepEditList.add(this.reportPreviewEditStep);
    }

    private createPostStepReportPreview() {
        this.reportPreviewPostStep = new ReportPreview(
            { reportPreviewTitle:  `${this.processType.title} Posting Report` },
            { id: "reportPreviewPostStep" }
        );
        this.stepPostingReports.add(this.reportPreviewPostStep);
    }

    continue() {
        if (this.stepperProcessing.selectedIndex < this.stepperProcessing.stepCount - 1) {
            this.stepperProcessing.selectedIndex += 1;
        }
    }

    /** This is an event handler for the beforeStepListener event of stepperProcessing. */
    stepperProcessingBeforeStepListener(event) {
        if (event.newIndex < event.oldIndex) {
            event.preventDefault();
            return;
        }
        this.buttonNextStep.visible = event.newIndex != this.stepperProcessing.stepCount - 1;
        if (event.newStep == this.stepPostingReports) {
            this.post();
        }
    }

    async showPreprocessDialog(): Promise<boolean> {
        if (this.postableCount > 0) {
            return await CommonDialogs.showYesNo(`${this.postableCount} records totaling $${this.postableAmount.amount.toFixed(2)} will be posted.`,
                `Post ${this.processType.title}`);
        } else {
            CommonDialogs.showMessage("No records to post.", `Post ${this.processType.title}`);
            return false;
        }
    }

    async post() {
        if (this.postableCount == null && this.postableAmount == null) {
                const { count, amount } = await this.calculatePostableTotals();
                this.postableCount = count;
                this.postableAmount = amount;
        }
        const response = await this.showPreprocessDialog();
        if (!!response) {
            this.reportPreviewPostStep.beginSearch();
            const response = await Api.post(this.processType.endpoint, this.getApiPayload(true));
            this.doAfterPost?.(response);
            this.stepPostingReports.checked = true;
            const responseFirstRow = response?.data?.[0];
            this.reportPreviewPostStep.endSearch(responseFirstRow?.reports);
        } else {
            this.slideOut?.();
        }
    }

    async displayReport() {
        this.reportPreviewEditStep.beginSearch();
        const response = await Api.search(this.processType.endpoint, this.getApiPayload(false));
        const responseFirstRow = response?.data?.[0];
        this.postableCount = responseFirstRow?.postable_count ?? 0 as number;
        this.postableAmount = responseFirstRow?.postable_amount ?? new Currency(0);
        const errorMsg = response?.error?.message ?? responseFirstRow?.error;
        if (errorMsg) {
            CommonDialogs.showMessage(errorMsg, "Error");
        }
        this.reportPreviewEditStep.endSearch(responseFirstRow?.reports);
    }

}
